import React, { useEffect, useMemo, useState } from "react";
import { getAllStudents, refreshStudentsFromFirestore } from "../../services/studentsService";
import { computeStudentFinance, matchLevelConfig, getPaymentModeConfig, computeTotalDue, parseMoneyAmount } from "../../utils/feeCalculator";
import "./StudentFees.css";
import CampusStudentTable from "../../components/CampusStudentTable";
import StatementOfAccount from "../../components/StatementOfAccount";
import { createTransaction } from "../../services/apiClient";
import { chartOfAccounts } from "../../data/chartOfAccounts";
import { getCurrentUser } from "../../auth";
// Removed unused import
// import MultiStatementOfAccount from "../../components/MultiStatementOfAccount";
import MultiSOAPrint from "../../components/MultiSOAPrint";
import { getPayments as fetchPayments, addPayment as addPaymentService } from "../../services/transactionsService";
import { db } from "../../services/firebase";
import { collection, doc, onSnapshot, setDoc } from "firebase/firestore";

export default function StudentFees() {
  const [students, setStudents] = useState([]);
  const [baseStudents, setBaseStudents] = useState(() => getAllStudents());
  // Removed unused state setters
  const [query, setQuery] = useState("");
  const [campus, setCampus] = useState("");
  const [grade, setGrade] = useState("");
  const [strand, setStrand] = useState("");
  // const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showSOA, setShowSOA] = useState(false);
  const [showMultiSOA, setShowMultiSOA] = useState(false);
  const [selectedStudentsForSOA, setSelectedStudentsForSOA] = useState([]);
  const [payments, setPayments] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [csvRows, setCsvRows] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvDefaults, setCsvDefaults] = useState(() => ({
    campus: "",
    method: "Cash",
    category: "Tuition",
    accountId: "",
    date: ""
  }));
  const [csvAlsoCreateReceipts, setCsvAlsoCreateReceipts] = useState(false);
  const [csvAllowUnknownStudents, setCsvAllowUnknownStudents] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "payments"), (snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPayments(rows);
    }, () => {
      // fallback one-time fetch
      (async () => {
        try {
          const rows = await fetchPayments();
          setPayments(Array.isArray(rows) ? rows : []);
        } catch {
          setPayments([]);
        }
      })();
    });
    return () => unsub();
  }, []);

  // Load accounts from localStorage for tagging
  const accounts = (() => {
    try {
      const raw = localStorage.getItem("accounts");
      return raw ? JSON.parse(raw) : chartOfAccounts;
    } catch {
      return chartOfAccounts;
    }
  })();
  const incomeAccounts = accounts.filter(a => a.type === "Income");
  const defaultAccountKey = incomeAccounts.length ? String(incomeAccounts[0].id ?? incomeAccounts[0].code ?? incomeAccounts[0].name) : "";
  const [selectedAccountId, setSelectedAccountId] = useState(defaultAccountKey);
  const [paymentCategory, setPaymentCategory] = useState("Tuition");

  // Track preferred payment mode per student
  const [paymentModesMap, setPaymentModesMap] = useState(() => {
    try {
      const raw = localStorage.getItem("studentPaymentModes");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const list = await refreshStudentsFromFirestore();
        if (Array.isArray(list) && list.length) {
          setBaseStudents(list);
        } else {
          setBaseStudents(getAllStudents());
        }
      } catch {
        setBaseStudents(getAllStudents());
      }
    })();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "students"), (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      try { localStorage.setItem("students_fs_cache", JSON.stringify(list)); } catch (e) { /* ignore */ }
      setBaseStudents(list);
    }, () => { /* ignore */ });
    return () => unsub();
  }, []);

  // Compute finance per student based on tuitionConfigs + payment mode
  useEffect(() => {
    const base = baseStudents;
    const computed = base.map((s) => {
      const mode = paymentModesMap[s.id] || s.paymentMode || "Annual";
      const studentPayments = payments.filter((p) => String(p.studentId || p.student_id || "") === String(s.id));
      let schedule = [];
      let totalDue = 0;

      // Compute from tuitionConfigs as the source of truth
      const finFromConfig = computeStudentFinance({ ...s, paymentMode: mode }, studentPayments, "Annual");
      const recomputedSchedule = Array.isArray(finFromConfig.schedule) ? finFromConfig.schedule.map(ent => ({
        period: ent.label || ent.period || "",
        misc: Number(ent.misc || 0),
        tuition: Number(ent.tuition || 0),
        total: Number(ent.total || (Number(ent.misc || 0) + Number(ent.tuition || 0))),
      })) : [];
      const recomputedTotalDue = Number(finFromConfig.totalDue || 0);

      // 1) If enrollment-saved schedule exists and matches config total, use it; otherwise prefer recomputed
      const hasSaved = s.paymentSchedule && Array.isArray(s.paymentSchedule.schedule);
      if (hasSaved) {
        const savedSchedule = s.paymentSchedule.schedule.map((ent) => ({
          period: ent.label || ent.period || "",
          misc: Number(ent.misc || 0),
          tuition: Number(ent.tuition || 0),
          total: Number(ent.total || (Number(ent.misc || 0) + Number(ent.tuition || 0))),
        }));
        const savedTotal = Number(s.paymentSchedule.totalDue || savedSchedule.reduce((sum, e) => sum + Number(e.total || 0), 0));
        if (Math.abs(savedTotal - recomputedTotalDue) < 0.5) {
          schedule = savedSchedule;
          totalDue = savedTotal;
        } else {
          // Saved is out of date (e.g., double-discounted) – use recomputed from configs
          schedule = recomputedSchedule;
          totalDue = recomputedTotalDue;
        }
      } else {
        schedule = recomputedSchedule;
        totalDue = recomputedTotalDue;
      }

      // 3) Last resort: direct derive from tuitionConfigs by campus/grade/mode if still empty
      if (schedule.length === 0) {
        const lvl = matchLevelConfig(s.campus, s.gradeLevel);
        const pm = getPaymentModeConfig(lvl, mode);
        if (pm && Array.isArray(pm.schedule)) {
          schedule = pm.schedule.map((ent) => ({
            period: ent.label || ent.period || "",
            misc: Number(ent.misc || 0),
            tuition: Number(ent.tuition || 0),
            total: Number(ent.total || (Number(ent.misc || 0) + Number(ent.tuition || 0))),
          }));
          totalDue = computeTotalDue(pm);
        }
      }

      const totalPaid = studentPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
      const outstanding = Math.max(0, totalDue - totalPaid);
      const finNext = computeStudentFinance({ ...s, paymentMode: mode }, studentPayments, "Annual");
      const nextDue = Number(finNext.nextDue || 0);
      const sectionStrand = (s.section ? s.section + " / " : "") + (s.strand || "");
      return { ...s, paymentMode: mode, totalDue, totalPaid, outstanding, schedule, nextDue, sectionStrand };
    });
    setStudents(computed);
    // If current selected student exists, refresh it from computed list
    setSelected((prev) => {
      if (!prev) return prev;
      const fresh = computed.find((x) => x.id === prev.id);
      return fresh || prev;
    });
  }, [payments, paymentModesMap, baseStudents]);

  const currentUser = getCurrentUser();
  const userCampus = String(currentUser?.campus || "").toLowerCase();
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";
  const campuses = useMemo(() => {
    const all = [...new Set(students.map(s => s.campus))];
    return isAdmin ? all : all.filter(c => String(c || "").toLowerCase() === userCampus);
  }, [students, isAdmin, userCampus]);
  const grades = useMemo(() => [...new Set(students.map(s => s.gradeLevel))], [students]);
  const strands = useMemo(() => [...new Set(students.map(s => s.strand).filter(Boolean))], [students]);

  // Define campusOptions for campus-separated tables
  const campusOptions = campuses.map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));

  const filtered = useMemo(() => {
    const base = isAdmin ? students : students.filter(s => String(s.campus || "").toLowerCase() === userCampus);
    return base.filter(s => {
      const q = query.trim().toLowerCase();
      if (q && !s.fullname.toLowerCase().includes(q) && !String(s.id).toLowerCase().includes(q)) return false;
      if (campus && s.campus !== campus) return false;
      if (grade && s.gradeLevel !== grade) return false;
      if (strand && s.strand !== strand) return false;
      return true;
    });
  }, [students, query, campus, grade, strand, isAdmin, userCampus]);

  // Removed unused variables
  // const accounts = ...;
  // const grades = useMemo(() => [...new Set(students.map(s => s.gradeLevel))], [students]);
  // const strands = useMemo(() => [...new Set(students.map(s => s.strand).filter(Boolean))], [students]);
  // const studentPayments = useMemo(() => {
  //   if (!selected) return [];
  //   return payments.filter(p => p.studentId === selected.id);
  // }, [payments, selected]);

  const [paymentForm, setPaymentForm] = useState({ amount: "", method: "Cash", reference: "", date: "" });
  const [applyPeriod, setApplyPeriod] = useState("");
  const [applyScope, setApplyScope] = useState("both");

  // Handle change of preferred payment mode per student
  function handleChangeMode(studentId, mode) {
    const updated = { ...paymentModesMap, [studentId]: mode };
    setPaymentModesMap(updated);
    try {
      localStorage.setItem("studentPaymentModes", JSON.stringify(updated));
    } catch (err) {
      console.warn(err && err.message ? err.message : "Failed to persist payment mode");
    }
  }

  // Suggest next due amount based on schedule vs total paid
  function computeNextDueAmount(sel) {
    if (!sel) return 0;
    if (typeof sel.nextDue === "number") return sel.nextDue;
    return Math.max(0, Number(sel.outstanding || 0));
  }

  // Auto-fill amount based on next due when selected or its schedule changes
  useEffect(() => {
    if (!selected) return;
    const suggested = computeNextDueAmount(selected);
    if (suggested > 0) {
      setPaymentForm((f) => ({ ...f, amount: String(suggested) }));
    }
  }, [selected?.id, selected?.paymentMode, JSON.stringify(selected?.schedule || []), payments]);

  // Handler to toggle student selection for multi SOA print
  function handleSelectStudentForSOA(student) {
    setSelectedStudentsForSOA(prev => {
      if (prev.some(s => s.id === student.id)) {
        return prev.filter(s => s.id !== student.id);
      } else {
        return [...prev, student];
      }
    });
  }

  // Handler to print multiple SOAs
  function handlePrintMultiSOA() {
    if (selectedStudentsForSOA.length === 0) {
      alert("Please select at least one student to print SOA.");
      return;
    }
    setShowMultiSOA(true);
  }

  // Handler to close multi SOA print view
  function handleCloseMultiSOA() {
    setShowMultiSOA(false);
  }

  const studentById = useMemo(() => {
    const m = new Map();
    (baseStudents || []).forEach(s => m.set(String(s.id || "").toLowerCase(), s));
    return m;
  }, [baseStudents]);
  const studentByName = useMemo(() => {
    const m = new Map();
    (baseStudents || []).forEach(s => {
      const n = ([s.firstname, s.middlename, s.lastname].filter(Boolean).join(" ").trim() || s.fullname || "").toLowerCase();
      if (n) m.set(n, s);
    });
    return m;
  }, [baseStudents]);
  const existingPaymentKeys = useMemo(() => {
    const set = new Set();
    (payments || []).forEach(p => {
      const c = String(p.campus || "").toLowerCase();
      const r = String(p.reference || "").trim();
      if (c && r) set.add(`${c}|${r}`);
    });
    return set;
  }, [payments]);

  const detectDelimiter = (text) => {
    const input = String(text || "").replace(/^\uFEFF/, "");
    let inQuotes = false;
    let commas = 0;
    let semis = 0;
    let tabs = 0;
    for (let i = 0; i < input.length; i++) {
      const ch = input[i];
      const next = input[i + 1];
      if (inQuotes) {
        if (ch === "\"" && next === "\"") { i++; continue; }
        if (ch === "\"") { inQuotes = false; continue; }
        continue;
      }
      if (ch === "\"") { inQuotes = true; continue; }
      if (ch === "\n") break;
      if (ch === ",") commas++;
      else if (ch === ";") semis++;
      else if (ch === "\t") tabs++;
    }
    const best = Math.max(commas, semis, tabs);
    if (best === 0) return ",";
    if (best === semis) return ";";
    if (best === tabs) return "\t";
    return ",";
  };

  const parseCsvText = (text) => {
    const input = String(text || "").replace(/^\uFEFF/, "");
    const delim = detectDelimiter(input);
    const rows = [];
    let row = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < input.length; i++) {
      const ch = input[i];
      const next = input[i + 1];
      if (inQuotes) {
        if (ch === "\"" && next === "\"") {
          cur += "\"";
          i++;
          continue;
        }
        if (ch === "\"") {
          inQuotes = false;
          continue;
        }
        cur += ch;
        continue;
      }
      if (ch === "\"") {
        inQuotes = true;
        continue;
      }
      if (ch === delim) {
        row.push(cur);
        cur = "";
        continue;
      }
      if (ch === "\n") {
        row.push(cur);
        cur = "";
        const hasAny = row.some(v => String(v || "").trim() !== "");
        if (hasAny) rows.push(row);
        row = [];
        continue;
      }
      if (ch === "\r") continue;
      cur += ch;
    }
    row.push(cur);
    if (row.some(v => String(v || "").trim() !== "")) rows.push(row);
    return rows;
  };
  const normalizeHeader = (h) => String(h || "").trim().toLowerCase().replace(/\s+/g, "_");
  const hash32 = (str) => {
    let h = 5381;
    const s = String(str || "");
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h) + s.charCodeAt(i);
    return (h >>> 0).toString(16);
  };
  const badRefChars = new RegExp("[/\\\\?#\\[\\]]", "g");
  const sanitizeRef = (ref) => String(ref || "").trim().replace(badRefChars, "-").slice(0, 120);
  const buildPaymentFromCsvRow = (rowObj) => {
    const rawStudentId = String(rowObj.studentid || rowObj.student_id || rowObj.id || "").trim();
    const rawStudentName = String(rowObj.studentname || rowObj.student_name || rowObj.fullname || rowObj.name || "").trim();
    const student =
      (rawStudentId && studentById.get(rawStudentId.toLowerCase())) ||
      (rawStudentName && studentByName.get(rawStudentName.toLowerCase())) ||
      null;

    const studentId = String(student?.id || rawStudentId || "").trim();
    const campusCode = String(rowObj.campus || csvDefaults.campus || student?.campus || "").toLowerCase();
    const method = String(rowObj.method || csvDefaults.method || "Cash").trim() || "Cash";
    const category = String(rowObj.category || csvDefaults.category || "Tuition").trim() || "Tuition";
    const date = String(rowObj.date || csvDefaults.date || "").trim();
    const amount = parseMoneyAmount(rowObj.amount ?? rowObj.payment ?? rowObj.paid ?? rowObj.total ?? 0);
    const refRaw = String(rowObj.reference || rowObj.or || rowObj.or_no || rowObj.or_number || "").trim();
    const accountIdRaw = String(rowObj.accountid || rowObj.account_id || csvDefaults.accountId || selectedAccountId || "").trim();

    const errors = [];
    if (!studentId) errors.push("Missing studentId/studentName");
    if (!student && !csvAllowUnknownStudents) errors.push("Student not found");
    if (!campusCode) errors.push("Missing campus");
    if (!date) errors.push("Missing date (YYYY-MM-DD)");
    if (!Number.isFinite(amount) || amount <= 0) errors.push("Invalid amount");

    const syntheticRef = sanitizeRef(`CSV-${studentId}-${date}-${hash32(JSON.stringify(rowObj))}`);
    const reference = sanitizeRef(refRaw || syntheticRef);
    const paymentKey = campusCode && reference ? `${campusCode}|${reference}` : "";

    const newPayment = {
      date,
      payer: String(student?.fullname || student?.name || rawStudentName || "").trim(),
      studentId,
      campus: campusCode || "gen",
      method,
      reference,
      amount: Number(amount || 0),
      accountId: accountIdRaw || "tuition",
      category,
      applyPeriod: String(rowObj.applyperiod || rowObj.apply_period || "").trim(),
      applyScope: String(rowObj.applyscope || rowObj.apply_scope || "").trim() || "both",
      createdAt: new Date().toISOString(),
      source: "student-fees-csv"
    };

    return { student, studentId, paymentKey, newPayment, errors };
  };

  const handleDownloadCsvTemplate = () => {
    const header = ["studentId", "amount", "date", "reference", "method", "campus", "category", "accountId"];
    const sample = ["S-GAPAN-0001", "2500", "2026-04-10", "69174", "Cash", "gapan", "Tuition", String(selectedAccountId || "tuition")];
    const csv = [header.join(","), sample.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "payments_import_template.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleParseCsv = async (file) => {
    setCsvErrors([]);
    setCsvRows([]);
    setCsvPreview([]);
    if (!file) return;
    const text = await file.text();
    const grid = parseCsvText(text);
    if (!grid.length) {
      setCsvErrors(["CSV is empty."]);
      return;
    }
    const header = grid[0].map(normalizeHeader);
    const dataRows = grid.slice(1);
    const rows = dataRows.map((cols) => {
      const obj = {};
      header.forEach((h, i) => { obj[h] = cols[i] ?? ""; });
      return obj;
    }).filter(o => Object.values(o).some(v => String(v || "").trim() !== ""));

    const errs = [];
    const parsed = rows.map((r, idx) => {
      const out = buildPaymentFromCsvRow(r);
      if (out.errors.length) errs.push(`Row ${idx + 2}: ${out.errors.join(", ")}`);
      if (out.paymentKey && existingPaymentKeys.has(out.paymentKey)) errs.push(`Row ${idx + 2}: Duplicate reference detected (${out.paymentKey})`);
      return { index: idx, raw: r, ...out };
    });
    setCsvRows(parsed);
    setCsvPreview(parsed.slice(0, 10));
    setCsvErrors(errs.slice(0, 30));
  };

  const runPool = async (items, worker, concurrency = 8) => {
    const results = new Array(items.length);
    let next = 0;
    const runners = new Array(Math.min(concurrency, items.length)).fill(null).map(async () => {
      while (next < items.length) {
        const i = next++;
        results[i] = await worker(items[i], i);
      }
    });
    await Promise.all(runners);
    return results;
  };

  const handleImportCsv = async () => {
    if (csvBusy) return;
    const valid = (csvRows || []).filter(r => r && r.newPayment && (!r.errors || r.errors.length === 0));
    if (valid.length === 0) {
      alert("No valid rows to import.");
      return;
    }
    setCsvBusy(true);
    try {
      const results = await runPool(valid, async (r) => {
        const p = r.newPayment;
        const key = `${String(p.campus || "").toLowerCase()}|${String(p.reference || "").trim()}`;
        try {
          await setDoc(doc(db, "payments", key), p, { merge: false });
          if (csvAlsoCreateReceipts) {
            try {
              await createTransaction({
                type: "receipt",
                date: p.date,
                category: p.category || "Tuition Revenue",
                accountId: p.accountId || null,
                amount: p.amount,
                method: p.method,
                reference: p.reference,
                studentId: String(p.studentId),
                campus: p.campus,
                notes: "Imported via Student Fees CSV"
              });
            } catch {
              // ignore
            }
          }
          return { ok: true, key };
        } catch (e) {
          return { ok: false, key, error: e?.message || "Write failed" };
        }
      }, 8);
      const ok = results.filter(r => r?.ok).length;
      const fail = results.filter(r => r && !r.ok).length;
      const failedLines = results.filter(r => r && !r.ok).slice(0, 10).map(r => `${r.key}: ${r.error}`);
      if (fail) alert(`Imported ${ok} payments. Failed: ${fail}.\n\n${failedLines.join("\n")}`);
      else alert(`Imported ${ok} payments.`);
      setCsvFile(null);
      setCsvRows([]);
      setCsvPreview([]);
      setCsvErrors([]);
      setCsvOpen(false);
    } finally {
      setCsvBusy(false);
    }
  };

  return (
    <>
      <div className="container-fluid student-fees-page">
        <div className="row">
          <div className="no-print d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-3">
              <h3 className="m-0">Student Fees</h3>
              {isAdmin && (
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0">Campus</label>
                  <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)} style={{ width: 160 }}>
                    <option value="">All</option>
                    <option value="gapan">gapan</option>
                    <option value="bulacan">bulacan</option>
                  </select>
                </div>
              )}
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0">Search</label>
                <input className="form-control" value={query} onChange={e => setQuery(e.target.value)} placeholder="Name or ID" style={{ width: 220 }} />
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-outline-primary" onClick={() => setCsvOpen(v => !v)}>
                {csvOpen ? "Close CSV Import" : "Import Payments (CSV)"}
              </button>
            </div>
          </div>
          {csvOpen && (
            <div className="no-print card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Import Payments via CSV</h6>
                <button className="btn btn-sm btn-outline-secondary" onClick={handleDownloadCsvTemplate}>Download Template</button>
              </div>
              <div className="row g-2 mt-2 align-items-end">
                <div className="col-md-4">
                  <label className="form-label">CSV File</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="form-control"
                    onChange={async (e) => {
                      const f = e.target.files?.[0] || null;
                      setCsvFile(f);
                      await handleParseCsv(f);
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Default Date</label>
                  <input type="date" className="form-control" value={csvDefaults.date} onChange={e => setCsvDefaults(d => ({ ...d, date: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Default Campus</label>
                  <select className="form-select" value={csvDefaults.campus} onChange={e => setCsvDefaults(d => ({ ...d, campus: e.target.value }))}>
                    <option value="">(from student)</option>
                    <option value="gapan">gapan</option>
                    <option value="bulacan">bulacan</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Default Method</label>
                  <select className="form-select" value={csvDefaults.method} onChange={e => setCsvDefaults(d => ({ ...d, method: e.target.value }))}>
                    <option>Cash</option>
                    <option>GCash</option>
                    <option>Bank</option>
                    <option>Check</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Default Category</label>
                  <select className="form-select" value={csvDefaults.category} onChange={e => setCsvDefaults(d => ({ ...d, category: e.target.value }))}>
                    <option>Tuition</option>
                    <option>Misc</option>
                    <option>Reservation</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="row g-2 mt-2 align-items-end">
                <div className="col-md-6">
                  <label className="form-label">Default Account</label>
                  <select className="form-select" value={csvDefaults.accountId} onChange={e => setCsvDefaults(d => ({ ...d, accountId: e.target.value }))}>
                    <option value="">(use current selection)</option>
                    {incomeAccounts.map(a => {
                      const key = String(a.id ?? a.code ?? a.name);
                      return <option key={key} value={key}>{a.name}</option>;
                    })}
                  </select>
                </div>
                <div className="col-md-3">
                  <div className="form-check mt-4">
                    <input className="form-check-input" type="checkbox" id="csvAlsoCreateReceipts" checked={csvAlsoCreateReceipts} onChange={e => setCsvAlsoCreateReceipts(e.target.checked)} />
                    <label className="form-check-label" htmlFor="csvAlsoCreateReceipts">Also create receipts (if API available)</label>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-check mt-4">
                    <input className="form-check-input" type="checkbox" id="csvAllowUnknownStudents" checked={csvAllowUnknownStudents} onChange={e => setCsvAllowUnknownStudents(e.target.checked)} />
                    <label className="form-check-label" htmlFor="csvAllowUnknownStudents">Allow unknown student IDs</label>
                  </div>
                </div>
                <div className="col-md-3 d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-primary"
                    disabled={csvBusy || !csvFile || csvRows.length === 0}
                    onClick={handleImportCsv}
                  >
                    {csvBusy ? "Importing..." : `Import ${csvRows.filter(r => !r.errors?.length).length} Payment(s)`}
                  </button>
                </div>
              </div>

              {csvErrors.length > 0 && (
                <div className="alert alert-warning mt-3 mb-0">
                  <div className="fw-bold mb-1">CSV Issues (showing up to 30)</div>
                  <ul className="mb-0">
                    {csvErrors.map((e, idx) => <li key={idx}>{e}</li>)}
                  </ul>
                </div>
              )}

              {csvPreview.length > 0 && (
                <div className="table-responsive mt-3">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Student</th>
                        <th>Student ID</th>
                        <th>Date</th>
                        <th>Reference</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((r) => (
                        <tr key={r.index}>
                          <td>{r.index + 2}</td>
                          <td>{r.student?.fullname || r.raw?.studentname || r.raw?.fullname || r.raw?.name || "-"}</td>
                          <td>{r.studentId || "-"}</td>
                          <td>{r.newPayment?.date || "-"}</td>
                          <td>{r.newPayment?.reference || "-"}</td>
                          <td className="text-end">₱{Number(r.newPayment?.amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="small text-muted">Preview shows first 10 rows only.</div>
                </div>
              )}
            </div>
          )}
          <div className={selected ? "col-lg-8" : "col-lg-12"}>
            {/* Campus-separated tables and overview logic here */}
            {campusOptions.map((campus) => {
              const campusStudents = filtered.filter(s => s.campus === campus.value);
              return (
                <CampusStudentTable
                  key={campus.value}
                  campus={campus.label}
                  students={campusStudents}
                  onViewStudent={setSelected}
                  selectedStudentsForSOA={selectedStudentsForSOA} // Pass the prop here
                  onSelectStudentForSOA={handleSelectStudentForSOA} // Pass the handler here
                />
              );
            })}

            {/* Button to print multiple SOAs */}
            <div className="mt-3">
              <button
                className="btn btn-primary"
                onClick={handlePrintMultiSOA}
                disabled={selectedStudentsForSOA.length === 0}
              >
                Print Selected SOAs
              </button>
            </div>

            {/* Multi SOA print view */}
            {showMultiSOA && (
              <div className="multi-soa-print-wrapper">
                <button
                  className="btn btn-secondary mb-3"
                  onClick={handleCloseMultiSOA}
                >
                  Close Print View
                </button>
                <button
                  className="btn btn-success mb-3 ms-2"
                  onClick={() => window.print()}
                >
                  Print Page
                </button>
                <MultiSOAPrint
                  selectedStudents={selectedStudentsForSOA}
                  generateSOA={(student) => (
                    <StatementOfAccount
                      key={student.id}
                      student={student}
                      payments={payments.filter(p => p.studentId === student.id)}
                      onClose={() => {}}
                    />
                  )}
                />
              </div>
            )}
          </div>

          {selected && (
            <div className="col-lg-4">
              <div className="card details-panel shadow-sm">
                <div className="card-header text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Student Details</h5>
                  <button
                    className="btn btn-sm btn-outline-light"
                    onClick={() => setSelected(null)}
                    title="Close"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <div className="card-body">
                  <div className="text-center mb-3">
                    <h6 className="fw-bold">{selected.fullname}</h6>
                    <div className="text-muted">
                      {selected.gradeLevel}
                      {selected.sectionStrand ? ` (Section/Strand: ${selected.sectionStrand})` : ""}
                      {!selected.sectionStrand && (selected.strand || selected.section) ? ` (Section/Strand: ${selected.section ? selected.section + " / " : ""}${selected.strand || ""})` : ""}
                      {selected.campus.charAt(0).toUpperCase() + selected.campus.slice(1)}
                    </div>
                  </div>

                  <div className="row text-center mb-3">
                    <div className="col-4">
                      <div className="summary-box">
                        <small className="text-muted">Total Due</small>
                        <div className="amount-display amount-due">₱{Number(selected.totalDue || 0).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="summary-box">
                        <small className="text-muted">Paid</small>
                        <div className="amount-display amount-paid">₱{Number(selected.totalPaid || 0).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="summary-box">
                        <small className="text-muted">Outstanding</small>
                        <div className={`amount-display ${selected.outstanding > 0 ? "amount-outstanding" : "text-muted"}`}>₱{Number(selected.outstanding || 0).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>

                  <h6>Payment Schedule ({selected.paymentMode})</h6>
                  {selected.schedule && selected.schedule.length > 0 ? (
                    <div className="table-responsive mb-3">
                      <table className="table schedule-table table-sm table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Period</th>
                            <th className="text-end">Misc</th>
                            <th className="text-end">Tuition</th>
                            <th className="text-end">Total</th>
                            {selected.paymentMode === "Annual" && <th className="text-end">Actual Payment</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {selected.schedule.map((sch, idx) => {
                            return (
                              <tr key={idx}>
                                <td>{sch.period || sch.label || `Period ${idx + 1}`}</td>
                                <td className="text-end">₱{Number(sch.misc || 0).toLocaleString()}</td>
                                <td className="text-end">₱{Number(sch.tuition || 0).toLocaleString()}</td>
                                <td className="text-end">₱{Number((sch.misc || 0) + (sch.tuition || 0)).toLocaleString()}</td>
                                {selected.paymentMode === "Annual" && <td className="text-end">₱{Number(sch.actualPayment || 0).toLocaleString()}</td>}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p>No payment schedule available.</p>
                  )}

                  <div className="row g-2">
                    <div className="col-12">
                      <label className="form-label small mb-1">Payment Mode</label>
                      <select
                        className="form-select form-select-sm"
                        value={paymentModesMap[selected.id] || selected.paymentMode || "Annual"}
                        onChange={e => handleChangeMode(selected.id, e.target.value)}
                      >
                        <option value="Annual">Annual</option>
                        <option value="Semestral">Semestral</option>
                        <option value="Installment">Installment</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Amount" 
                        value={paymentForm.amount} 
                        onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} 
                      />
                    </div>
                    <div className="col-6">
                      <select 
                        className="form-select form-select-sm" 
                        value={paymentForm.method} 
                        onChange={e => setPaymentForm({ ...paymentForm, method: e.target.value })}
                      >
                        <option>Cash</option>
                        <option>Bank Transfer</option>
                        <option>GCash</option>
                        <option>Check</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <select
                        className="form-select form-select-sm"
                        value={selectedAccountId}
                        onChange={e => setSelectedAccountId(e.target.value)}
                      >
                        <option value="">Select Income Account</option>
                        {incomeAccounts.map(acc => (
                          <option key={String(acc.id ?? acc.code ?? acc.name)} value={String(acc.id ?? acc.code ?? acc.name)}>{acc.name}</option>
                        ))}
                      </select>
                    </div>
                  <div className="col-6">
                    <select
                      className="form-select form-select-sm"
                      value={applyPeriod}
                      onChange={e => setApplyPeriod(e.target.value)}
                    >
                      <option value="">Apply Period (auto)</option>
                      {["Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <select
                      className="form-select form-select-sm"
                      value={applyScope}
                      onChange={e => setApplyScope(e.target.value)}
                    >
                      <option value="both">Apply To: Misc + Tuition</option>
                      <option value="misc">Apply To: Misc only</option>
                      <option value="tuition">Apply To: Tuition only</option>
                    </select>
                  </div>
                    <div className="col-12">
                      <select
                        className="form-select form-select-sm"
                        value={paymentCategory}
                        onChange={e => {
                          const v = e.target.value;
                          setPaymentCategory(v);
                          if (/tuition/i.test(v)) setApplyScope("tuition");
                          else if (/certificate|other/i.test(v)) setApplyScope("misc");
                          else setApplyScope("both");
                        }}
                      >
                        <option value="Tuition">Tuition</option>
                        <option value="Certificate">Certificate</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <input 
                        className="form-control form-control-sm" 
                        placeholder="OR Number" 
                        value={paymentForm.reference} 
                        onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })} 
                      />
                    </div>
                    <div className="col-6">
                      <input 
                        type="date" 
                        className="form-control form-control-sm" 
                        placeholder="mm/dd/yyyy" 
                        value={paymentForm.date} 
                        onChange={e => setPaymentForm({ ...paymentForm, date: e.target.value })} 
                      />
                    </div>
                  </div>
                  <button 
                    className="btn btn-primary btn-sm mt-3 w-100 record-payment-btn" 
                    disabled={!paymentForm.amount || !paymentForm.date}
                    onClick={handleRecordPayment}
                  >
                    Record Payment
                  </button>
                  
                  <button
                    className="btn btn-secondary btn-sm mt-2 w-100"
                    onClick={() => setShowSOA(true)}
                  >
                    <i className="bi bi-file-text me-1"></i> View Statement of Account
                  </button>
                  
                  {showSOA && (
                    <div className="mt-3">
                      <StatementOfAccount
                        student={selected}
                        payments={payments}
                        onClose={() => setShowSOA(false)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Add the missing handleRecordPayment function inside the StudentFees component
  async function handleRecordPayment() {
    if (!selected) return;
    const campusCode = String(selected.campus || "").toLowerCase() || "gen";
    const seqStore = JSON.parse(localStorage.getItem("or_sequences") || "{}");
    const nextSeq = Number(seqStore[campusCode] || 0) + 1;
    const orRef = paymentForm.reference && paymentForm.reference.trim()
      ? paymentForm.reference.trim()
      : `OR-${campusCode.toUpperCase()}-${String(nextSeq).padStart(4, "0")}`;
    const tuitionAccount = accounts.find(a => a.type === "Income" && /tuition/i.test(a.name || "")) || null;
    const chosenAccount = accounts.find(a => {
      const key = String(selectedAccountId || "");
      return String(a.id ?? a.code ?? a.name) === key;
    }) || tuitionAccount || null;
    const newPayment = {
      date: paymentForm.date,
      payer: selected.fullname || selected.name || "",
      studentId: selected.id,
      campus: campusCode,
      method: paymentForm.method,
      reference: orRef,
      amount: Number(paymentForm.amount || 0),
      accountId: String(chosenAccount ? (chosenAccount.id ?? chosenAccount.code ?? chosenAccount.name) : "tuition"),
      category: paymentCategory || "Tuition",
      applyPeriod: applyPeriod || "",
      applyScope: applyScope || "both",
      createdAt: new Date().toISOString(),
      source: "student-fees"
    };
    try {
      const rows = await addPaymentService(newPayment);
      setPayments(Array.isArray(rows) ? rows : []);
    } catch (e) {
      alert("Failed to record payment. Please try again.");
      return;
    }
    seqStore[campusCode] = nextSeq;
    localStorage.setItem("or_sequences", JSON.stringify(seqStore));
    setPaymentForm({ amount: "", method: "Cash", reference: "", date: "" });
    setApplyPeriod("");
    setApplyScope("both");
    // Do not auto-write to collections; Daily Cash Flow remains the sole entry point for collections.
    try {
      createTransaction({
        type: "receipt",
        date: newPayment.date,
        category: chosenAccount ? chosenAccount.name : "Tuition Revenue",
        accountId: chosenAccount ? chosenAccount.id : null,
        amount: newPayment.amount,
        method: newPayment.method,
        reference: newPayment.reference,
        studentId: String(selected.id),
        campus: selected.campus,
        notes: "Recorded via Student Fees"
      }).catch(() => {});
    } catch (err) {
      // ignore
    }
  }
}
